import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// ─── Types ────────────────────────────────────────────────────────────────────
type PuzzlePiece = {
  id: string;
  label: string;
  emoji: string;
  completed: boolean;
  reason: string;
};

// ─── Shared Code Challenge Component ──────────────────────────────────────────
interface CodeChallengeProps {
  title: string;
  emoji: string;
  code: string;
  filename: string;
  bugLines: Record<number, string>;
  language: string;
  onComplete: () => void;
  completed: boolean;
}

const CodeChallenge = ({
  title,
  emoji,
  code,
  filename,
  bugLines,
  language,
  onComplete,
  completed
}: CodeChallengeProps) => {
  const [consoleOutput, setConsoleOutput] = useState<string[]>([`> Analyze the code... Click the problematic lines!`]);
  const [found, setFound] = useState<number[]>([]);
  const [shaking, setShaking] = useState(false);
  const totalBugs = Object.keys(bugLines).length;
  const score = Math.round((found.length / totalBugs) * 100);

  useEffect(() => {
    if (found.length === totalBugs && !completed) {
      onComplete();
    }
  }, [found, totalBugs, completed, onComplete]);

  const handleLineClick = (lineNumber: number) => {
    if (bugLines[lineNumber]) {
      if (!found.includes(lineNumber)) {
        setFound([...found, lineNumber]);
        setConsoleOutput((prev) => [
          ...prev,
          `✅ Line ${lineNumber}: ${bugLines[lineNumber]}`,
          `> Progress: ${found.length + 1}/${totalBugs}`
        ]);
      }
    } else {
      setShaking(true);
      setConsoleOutput((prev) => [...prev, `❌ Line ${lineNumber}: Not the issue here! 💥`]);
      setTimeout(() => setShaking(false), 500);
    }
  };

  const resetGame = () => {
    setFound([]);
    setConsoleOutput([`> Reset! Find all ${totalBugs} issues...`]);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-bold text-white flex items-center gap-2">
          <span className="text-3xl">{emoji}</span> {title}
        </h3>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-sm font-mono">
            {found.length}/{totalBugs} found
          </span>
          {completed && (
            <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-300 text-sm">✓ Complete</span>
          )}
          <button
            onClick={resetGame}
            className="px-3 py-1 rounded-lg bg-coral-500/20 text-coral-400 text-sm hover:bg-coral-500/30 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div
          animate={shaking ? { x: [-5, 5, -5, 5, 0] } : {}}
          transition={{ duration: 0.4 }}
          className="rounded-xl overflow-hidden border border-white/10 bg-[#1e1e1e]"
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-[#2d2d2d] border-b border-white/5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ml-2 text-xs text-gray-400 font-mono">{filename}</span>
          </div>
          <div className="relative">
            {code.split('\n').map((line, i) => (
              <div
                key={i}
                onClick={() => handleLineClick(i + 1)}
                className={`flex items-center px-2 py-0.5 cursor-pointer transition-all duration-200 hover:bg-white/5 ${
                  found.includes(i + 1) ? 'bg-green-500/10 border-l-2 border-green-400' : ''
                }`}
              >
                <span className="w-8 text-right text-xs text-gray-500 mr-3 select-none font-mono">{i + 1}</span>
                <SyntaxHighlighter
                  language={language}
                  style={vscDarkPlus}
                  customStyle={{ background: 'transparent', padding: 0, margin: 0, fontSize: '0.8rem' }}
                  wrapLines={false}
                >
                  {line || ' '}
                </SyntaxHighlighter>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0d1117] flex flex-col">
          <div className="flex items-center gap-2 px-4 py-2 bg-[#161b22] border-b border-white/5">
            <span className="text-xs text-gray-400 font-mono">⬢ Analysis Output</span>
          </div>
          <div className="p-4 flex-1 overflow-y-auto max-h-[350px] font-mono text-sm space-y-1">
            {consoleOutput.map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`${line.startsWith('✅') ? 'text-green-400' : line.startsWith('❌') ? 'text-red-400' : 'text-gray-400'}`}
              >
                {line}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {score === 100 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-4 p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-center"
        >
          <span className="text-2xl">🎉</span>
          <p className="text-green-300 font-bold mt-1">Module complete! Puzzle piece unlocked.</p>
        </motion.div>
      )}
    </div>
  );
};

// ─── Challenge Data ───────────────────────────────────────────────────────────
const CHALLENGE_1 = {
  title: 'Bug Hunt: Async & Syntax',
  emoji: '🐛',
  filename: 'fetchUser.js',
  language: 'javascript',
  code: `function fetchUserData(userId) {
  const response = await fetch('/api/users/' + userId);
  const data = response.json();
  if (data.status = 200) {
    return data.body;
  }
  cosnt errorMsg = 'Failed to fetch';
  throw new Error(errorMsg);
}`,
  bugLines: {
    1: 'Missing "async" keyword — await used without async function declaration',
    3: 'Missing "await" — response.json() returns a Promise, not the resolved value',
    4: 'Assignment (=) instead of strict comparison (===) — always truthy!',
    7: 'Typo: "cosnt" should be "const" — will throw ReferenceError'
  } as Record<number, string>
};

const CHALLENGE_2 = {
  title: 'Scope & Hoisting Audit',
  emoji: '🔬',
  filename: 'scopeIssues.js',
  language: 'javascript',
  code: `console.log(userName);
var userName = 'Sam';

function processItems(items) {
  for (var i = 0; i < items.length; i++) {
    setTimeout(() => console.log(items[i]), 100);
  }
}

function createCounter() {
  let count = 0;
  return {
    increment: () => count++,
    getCount: () => count,
    reset: () => count = 0
  };
}

const result = multiply(5, 3);
const multiply = (a, b) => a * b;`,
  bugLines: {
    1: 'Hoisting trap: var userName is hoisted but undefined here — logs "undefined" not an error',
    5: 'Classic closure bug: var i is function-scoped, all timeouts will log items[items.length] = undefined',
    6: 'Related: setTimeout captures reference to i, not value — use let or IIFE to fix',
    19: 'TDZ error: const/let are NOT hoisted like var — multiply is in temporal dead zone here',
    20: 'Arrow functions assigned to const are NOT hoisted — unlike function declarations'
  } as Record<number, string>
};

const CHALLENGE_3 = {
  title: 'Idempotency & Transactions',
  emoji: '🏦',
  filename: 'paymentService.ts',
  language: 'typescript',
  code: `async function processPayment(orderId: string, amount: number) {
  const order = await db.orders.findById(orderId);
  order.status = 'processing';
  await db.orders.save(order);

  const payment = await gateway.charge(amount);
  order.status = 'paid';
  await db.orders.save(order);

  await emailService.sendReceipt(order);
  return { success: true };
}

async function retryPayment(orderId: string) {
  const order = await db.orders.findById(orderId);
  if (order.status !== 'pending') {
    return processPayment(orderId, order.amount);
  }
}

async function transferFunds(from: string, to: string, amount: number) {
  await db.accounts.debit(from, amount);
  await db.accounts.credit(to, amount);
}`,
  bugLines: {
    1: 'Not idempotent: no idempotency key — retrying this will charge the customer multiple times!',
    6: 'No transaction: if charge succeeds but save fails, money is taken but order stays "processing"',
    9: 'Side effect outside transaction: if email fails, should the whole payment rollback? No error handling',
    16: 'Broken idempotency check: status !== "pending" means ALREADY processed, yet we process AGAIN',
    21: 'No atomicity: if debit succeeds but credit fails, money disappears — needs a DB transaction wrapper'
  } as Record<number, string>
};

// ─── Enhanced Dev Journey ─────────────────────────────────────────────────────
const JOURNEY_STEPS = [
  {
    id: 1,
    label: 'Plan',
    emoji: '📋',
    desc: 'Requirements gathering & architecture decisions',
    challenge: 'What would YOU prioritize first?',
    options: ['User Stories', 'Tech Stack', 'Database Schema', 'API Design'],
    correctIdx: 0,
    explanation: 'User stories define WHAT we build. Everything else follows from understanding the user needs first.'
  },
  {
    id: 2,
    label: 'Code',
    emoji: '💻',
    desc: 'Implementation with best practices',
    challenge: 'Which pattern fits a payment system?',
    options: ['Singleton', 'Observer', 'Strategy', 'Command + Saga'],
    correctIdx: 3,
    explanation:
      'Command + Saga handles distributed transactions with rollback — critical for payments across services.'
  },
  {
    id: 3,
    label: 'Review',
    emoji: '👀',
    desc: 'Code review & pair programming',
    challenge: "What's the #1 thing to check in a PR?",
    options: ['Code style', 'Business logic correctness', 'Variable names', 'File size'],
    correctIdx: 1,
    explanation:
      "Linters handle style. The human reviewer's job is verifying the logic actually solves the problem correctly."
  },
  {
    id: 4,
    label: 'Test',
    emoji: '🧪',
    desc: 'Unit, integration & E2E testing',
    challenge: 'Which test catches the most real bugs?',
    options: ['Unit tests', 'Integration tests', 'E2E tests', 'Manual QA'],
    correctIdx: 1,
    explanation:
      'Integration tests catch bugs at boundaries between systems — where most production issues actually live.'
  },
  {
    id: 5,
    label: 'Deploy',
    emoji: '🚀',
    desc: 'CI/CD pipelines & production release',
    challenge: 'Best deployment strategy for zero downtime?',
    options: ['Big Bang', 'Blue-Green', 'Recreate', 'FTP Upload'],
    correctIdx: 1,
    explanation:
      'Blue-Green keeps the old version running while the new one warms up. Instant rollback if something breaks.'
  },
  {
    id: 6,
    label: 'Monitor',
    emoji: '📊',
    desc: 'Observability, alerts & incident response',
    challenge: 'First thing to check when an alert fires?',
    options: ['Restart the server', 'Check recent deploys', 'Blame someone', 'Read the logs'],
    correctIdx: 1,
    explanation: 'Most incidents correlate with recent changes. Check what was deployed in the last hour first.'
  }
];

const DevJourney = ({ onComplete, completed }: { onComplete: () => void; completed: boolean }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const allAnswered = Object.keys(answers).length === JOURNEY_STEPS.length;

  useEffect(() => {
    if (allAnswered && !completed) {
      onComplete();
    }
  }, [allAnswered, completed, onComplete]);

  const handleAnswer = (stepIdx: number, optionIdx: number) => {
    if (answers[stepIdx] !== undefined) return;
    setAnswers({ ...answers, [stepIdx]: optionIdx });
  };

  const correctCount = Object.entries(answers).filter(
    ([stepIdx, ans]) => ans === JOURNEY_STEPS[parseInt(stepIdx)].correctIdx
  ).length;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-white flex items-center gap-2">
          <span className="text-3xl">🗺️</span> Dev Journey — Your Turn
        </h3>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-sm font-mono">
            {Object.keys(answers).length}/{JOURNEY_STEPS.length} answered
          </span>
          {completed && (
            <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-300 text-sm">✓ Complete</span>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative flex items-center justify-between mb-8">
        <div className="absolute top-1/2 left-0 right-0 h-1 bg-white/10 -translate-y-1/2 rounded-full" />
        <motion.div
          className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-indigo-500 to-coral-500 -translate-y-1/2 rounded-full"
          animate={{ width: `${(activeStep / (JOURNEY_STEPS.length - 1)) * 100}%` }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        />
        {JOURNEY_STEPS.map((step, i) => (
          <motion.button
            key={step.id}
            onClick={() => setActiveStep(i)}
            className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all duration-300 ${
              answers[i] !== undefined
                ? answers[i] === step.correctIdx
                  ? 'bg-green-500/30 border-2 border-green-400 shadow-lg shadow-green-500/20'
                  : 'bg-coral-500/30 border-2 border-coral-400'
                : i === activeStep
                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/30 scale-110'
                  : 'bg-white/10 hover:bg-white/20'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {step.emoji}
          </motion.button>
        ))}
      </div>

      {/* Active Step with Interactive Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">{JOURNEY_STEPS[activeStep].emoji}</span>
            <div>
              <h4 className="text-xl font-bold text-white">{JOURNEY_STEPS[activeStep].label}</h4>
              <p className="text-gray-400">{JOURNEY_STEPS[activeStep].desc}</p>
            </div>
          </div>

          <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/5">
            <p className="text-indigo-300 font-medium mb-3">💡 {JOURNEY_STEPS[activeStep].challenge}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {JOURNEY_STEPS[activeStep].options.map((option, optIdx) => {
                const answered = answers[activeStep] !== undefined;
                const isCorrect = optIdx === JOURNEY_STEPS[activeStep].correctIdx;
                const wasSelected = answers[activeStep] === optIdx;

                return (
                  <motion.button
                    key={option}
                    onClick={() => handleAnswer(activeStep, optIdx)}
                    disabled={answered}
                    className={`p-3 rounded-lg text-left text-sm transition-all ${
                      answered
                        ? isCorrect
                          ? 'bg-green-500/20 border border-green-500/50 text-green-300'
                          : wasSelected
                            ? 'bg-red-500/20 border border-red-500/50 text-red-300'
                            : 'bg-white/5 border border-white/5 text-gray-500'
                        : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-indigo-500/30 cursor-pointer'
                    }`}
                    whileHover={!answered ? { scale: 1.02 } : {}}
                    whileTap={!answered ? { scale: 0.98 } : {}}
                  >
                    {answered && isCorrect && '✅ '}
                    {answered && wasSelected && !isCorrect && '❌ '}
                    {option}
                  </motion.button>
                );
              })}
            </div>

            {answers[activeStep] !== undefined && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 text-sm text-gray-300 italic border-l-2 border-indigo-500 pl-3"
              >
                {JOURNEY_STEPS[activeStep].explanation}
              </motion.p>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {allAnswered && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-4 p-4 rounded-xl bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 text-center"
        >
          <p className="text-white font-bold">
            Score: {correctCount}/{JOURNEY_STEPS.length} correct —{' '}
            {correctCount >= 4
              ? 'Senior material! 🔥'
              : correctCount >= 2
                ? 'Solid foundation! 💪'
                : 'Keep learning! 📚'}
          </p>
        </motion.div>
      )}
    </div>
  );
};

// ─── AI Co-op Mini Game: Robot Assistant + Developer Orchestrator ──────────────
type TaskItem = {
  id: number;
  description: string;
  type: 'human' | 'robot' | 'both';
  emoji: string;
  difficulty: 'easy' | 'medium' | 'hard';
};

const AI_TASKS: TaskItem[] = [
  { id: 1, description: 'Write unit tests for edge cases', type: 'robot', emoji: '🧪', difficulty: 'easy' },
  { id: 2, description: 'Design system architecture', type: 'human', emoji: '🏗️', difficulty: 'hard' },
  { id: 3, description: 'Refactor legacy spaghetti code', type: 'both', emoji: '🍝', difficulty: 'medium' },
  { id: 4, description: 'Generate API documentation', type: 'robot', emoji: '📝', difficulty: 'easy' },
  { id: 5, description: 'Decide on database schema', type: 'human', emoji: '🗄️', difficulty: 'hard' },
  { id: 6, description: 'Code review for security issues', type: 'both', emoji: '🔒', difficulty: 'medium' },
  { id: 7, description: 'Write boilerplate CRUD endpoints', type: 'robot', emoji: '⚡', difficulty: 'easy' },
  { id: 8, description: 'Handle ambiguous product requirements', type: 'human', emoji: '🤔', difficulty: 'hard' },
  { id: 9, description: 'Optimize database queries', type: 'both', emoji: '🚀', difficulty: 'medium' },
  { id: 10, description: 'Generate regex patterns', type: 'robot', emoji: '🔤', difficulty: 'easy' },
  { id: 11, description: 'Negotiate technical debt priorities', type: 'human', emoji: '⚖️', difficulty: 'hard' },
  { id: 12, description: 'Debug production memory leak', type: 'both', emoji: '🐛', difficulty: 'medium' }
];

const DraggableIcon = ({ type, emoji, label }: { type: 'human' | 'robot' | 'both'; emoji: string; label: string }) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('assignee', type);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="flex flex-col items-center gap-1 cursor-grab active:cursor-grabbing select-none"
    >
      <motion.div
        className="w-14 h-14 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-2xl hover:bg-white/15 hover:border-indigo-500/40 transition-all shadow-lg"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {emoji}
      </motion.div>
      <span className="text-[10px] text-gray-400 font-medium">{label}</span>
    </div>
  );
};

const AICoopGame = ({ onComplete, completed }: { onComplete: () => void; completed: boolean }) => {
  const [currentTask, setCurrentTask] = useState(0);
  const [assignments, setAssignments] = useState<Record<number, 'human' | 'robot' | 'both'>>({});
  const [showFeedback, setShowFeedback] = useState<{ correct: boolean; message: string } | null>(null);
  const [robotMood, setRobotMood] = useState<'idle' | 'happy' | 'thinking' | 'sad'>('idle');
  const [streak, setStreak] = useState(0);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const tasks = AI_TASKS.slice(0, 8);

  const allAssigned = Object.keys(assignments).length === tasks.length;

  useEffect(() => {
    if (allAssigned && !completed) {
      onComplete();
    }
  }, [allAssigned, completed, onComplete]);

  const assignTask = (taskIdx: number, assignee: 'human' | 'robot' | 'both') => {
    if (assignments[taskIdx] !== undefined) return;

    const task = tasks[taskIdx];
    const correct = assignee === task.type;

    setAssignments({ ...assignments, [taskIdx]: assignee });

    if (correct) {
      setRobotMood('happy');
      setStreak((s) => s + 1);
      setShowFeedback({ correct: true, message: getCorrectMessage(task, assignee) });
    } else {
      setRobotMood('sad');
      setStreak(0);
      setShowFeedback({ correct: false, message: getWrongMessage(task) });
    }

    setTimeout(() => {
      setShowFeedback(null);
      setRobotMood('idle');
      if (taskIdx < tasks.length - 1) setCurrentTask(taskIdx + 1);
    }, 2000);
  };

  const handleDrop = (e: React.DragEvent, taskIdx: number) => {
    e.preventDefault();
    setDragOver(null);
    const assignee = e.dataTransfer.getData('assignee') as 'human' | 'robot' | 'both';
    if (assignee) {
      assignTask(taskIdx, assignee);
    }
  };

  const handleDragOver = (e: React.DragEvent, taskIdx: number) => {
    e.preventDefault();
    if (assignments[taskIdx] === undefined) {
      setDragOver(taskIdx);
    }
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const getCorrectMessage = (task: TaskItem, assignee: string) => {
    if (assignee === 'robot') return `Perfect! AI handles "${task.description}" efficiently — no creativity needed.`;
    if (assignee === 'human') return `Exactly! "${task.description}" requires human judgment and context.`;
    return `Right! "${task.description}" works best as a human-AI collaboration.`;
  };

  const getWrongMessage = (task: TaskItem) => {
    if (task.type === 'robot') return `This is repetitive/generative work — let the AI handle it!`;
    if (task.type === 'human') return `This needs human creativity, empathy, or strategic thinking.`;
    return `This one needs BOTH — human oversight with AI speed.`;
  };

  const correctCount = Object.entries(assignments).filter(([idx, ans]) => ans === tasks[parseInt(idx)].type).length;

  const robotFace = {
    idle: '🤖',
    happy: '😄',
    thinking: '🤔',
    sad: '😅'
  };

  return (
    <div className="w-full">
      <h3 className="text-2xl font-bold text-white flex items-center gap-2 mb-2">
        <span className="text-3xl">🤖</span> AI Co-op: Who Does What?
      </h3>
      <p className="text-gray-400 mb-4 text-sm">
        Drag the right icon into each task&apos;s drop zone to assign it. Who should handle it — You, the AI, or Both?
      </p>

      {/* Draggable Icons Dock */}
      <div className="flex items-center justify-center gap-8 mb-8 p-4 rounded-xl bg-white/5 border border-white/10 border-dashed">
        <span className="text-xs text-gray-500 uppercase tracking-wider mr-2">Drag →</span>
        <DraggableIcon type="human" emoji="🧑" label="Me" />
        <DraggableIcon type="robot" emoji="🤖" label="Robot" />
        <DraggableIcon type="both" emoji="🤝" label="Both" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task Queue */}
        <div className="lg:col-span-2 space-y-3">
          {tasks.map((task, idx) => {
            const assigned = assignments[idx] !== undefined;
            const isActive = idx === currentTask && !allAssigned;
            const wasCorrect = assigned && assignments[idx] === task.type;
            const isDraggedOver = dragOver === idx && !assigned;

            return (
              <div
                key={task.id}
                onDrop={(e) => handleDrop(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragLeave={handleDragLeave}
                className={`p-4 rounded-xl border transition-all ${
                  isDraggedOver
                    ? 'bg-indigo-500/20 border-indigo-400/60 shadow-lg shadow-indigo-500/10 scale-[1.02]'
                    : isActive
                      ? 'bg-indigo-500/10 border-indigo-500/40 shadow-lg shadow-indigo-500/5'
                      : assigned
                        ? wasCorrect
                          ? 'bg-green-500/5 border-green-500/20'
                          : 'bg-red-500/5 border-red-500/20'
                        : 'bg-white/5 border-white/5 opacity-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{task.emoji}</span>
                    <div>
                      <p
                        className={`text-sm font-medium ${isActive || isDraggedOver ? 'text-white' : 'text-gray-400'}`}
                      >
                        {task.description}
                      </p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          task.difficulty === 'easy'
                            ? 'bg-green-500/20 text-green-300'
                            : task.difficulty === 'medium'
                              ? 'bg-yellow-500/20 text-yellow-300'
                              : 'bg-red-500/20 text-red-300'
                        }`}
                      >
                        {task.difficulty}
                      </span>
                    </div>
                  </div>

                  {/* Drop Zone / Result */}
                  {!assigned ? (
                    <div
                      className={`w-14 h-14 rounded-xl border-2 border-dashed flex items-center justify-center transition-all ${
                        isDraggedOver
                          ? 'border-indigo-400 bg-indigo-500/20 text-indigo-300'
                          : isActive
                            ? 'border-white/30 bg-white/5 text-gray-500'
                            : 'border-white/10 bg-white/[0.02] text-gray-600'
                      }`}
                    >
                      {isDraggedOver ? (
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-lg">
                          ✦
                        </motion.span>
                      ) : (
                        <span className="text-xs">Drop</span>
                      )}
                    </div>
                  ) : (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl border ${
                        wasCorrect ? 'bg-green-500/20 border-green-500/40' : 'bg-red-500/20 border-red-500/40'
                      }`}
                    >
                      {assignments[idx] === 'human' ? '🧑' : assignments[idx] === 'robot' ? '🤖' : '🤝'}
                    </motion.div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Robot Companion Panel */}
        <div className="space-y-4">
          <div className="p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm text-center">
            <motion.div
              animate={
                robotMood === 'happy'
                  ? { rotate: [0, -5, 5, -5, 0], scale: [1, 1.1, 1] }
                  : robotMood === 'sad'
                    ? { y: [0, 5, 0] }
                    : {}
              }
              transition={{ duration: 0.5 }}
              className="text-6xl mb-3"
            >
              {robotFace[robotMood]}
            </motion.div>
            <p className="text-white font-bold">AI Assistant</p>
            <p className="text-gray-400 text-xs mt-1">Your coding partner</p>

            {streak > 2 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-3 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 text-xs inline-block"
              >
                🔥 {streak} streak!
              </motion.div>
            )}
          </div>

          {/* Feedback */}
          <AnimatePresence>
            {showFeedback && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`p-4 rounded-xl border text-sm ${
                  showFeedback.correct
                    ? 'bg-green-500/10 border-green-500/30 text-green-300'
                    : 'bg-coral-500/10 border-coral-500/30 text-coral-400'
                }`}
              >
                {showFeedback.message}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Score */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <p className="text-gray-400 text-xs mb-2">Orchestration Score</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-indigo-500 to-green-400 rounded-full"
                  animate={{ width: `${(correctCount / tasks.length) * 100}%` }}
                />
              </div>
              <span className="text-white text-sm font-mono">
                {correctCount}/{tasks.length}
              </span>
            </div>
          </div>

          {allAssigned && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 text-center"
            >
              <p className="text-white font-bold text-sm">
                {correctCount >= 6
                  ? '🎯 Master Orchestrator!'
                  : correctCount >= 4
                    ? '👍 Good delegation!'
                    : '📚 AI boundaries take practice!'}
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Puzzle Reveal System ─────────────────────────────────────────────────────
const PUZZLE_PIECES: PuzzlePiece[] = [
  {
    id: 'bugs',
    label: 'Syntax & Async',
    emoji: '🐛',
    completed: false,
    reason:
      'Foundation: Before writing features, you must catch basic errors. This is the first filter in any code review — does it even run?'
  },
  {
    id: 'scope',
    label: 'Scope & Hoisting',
    emoji: '🔬',
    completed: false,
    reason:
      'Deep Knowledge: Understanding JS internals prevents subtle bugs that slip past linters. This separates juniors from seniors.'
  },
  {
    id: 'transactions',
    label: 'Idempotency & Transactions',
    emoji: '🏦',
    completed: false,
    reason:
      'Business Critical: Real apps handle money and data. Getting transactions wrong means lost revenue and broken trust.'
  },
  {
    id: 'journey',
    label: 'Dev Lifecycle',
    emoji: '🗺️',
    completed: false,
    reason:
      'Process: Code quality means nothing without a solid SDLC. Plan → Code → Review → Test → Deploy → Monitor is the backbone.'
  },
  {
    id: 'ai',
    label: 'AI Orchestration',
    emoji: '🤖',
    completed: false,
    reason:
      'The Future: Knowing WHEN to use AI and when to think yourself is the meta-skill of modern engineering. You orchestrate, AI executes.'
  }
];

const PuzzleTracker = ({ pieces }: { pieces: PuzzlePiece[] }) => {
  const completedCount = pieces.filter((p) => p.completed).length;
  const allComplete = completedCount === pieces.length;

  return (
    <div className="w-full">
      <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">🧩 Developer DNA Puzzle</h3>
          <span className="text-sm text-gray-400 font-mono">
            {completedCount}/{pieces.length}
          </span>
        </div>

        {/* Puzzle Grid */}
        <div className="grid grid-cols-5 gap-2 mb-4">
          {pieces.map((piece) => (
            <motion.div
              key={piece.id}
              animate={piece.completed ? { scale: [1, 1.1, 1], rotateY: [0, 180, 360] } : {}}
              transition={{ duration: 0.6 }}
              className={`aspect-square rounded-xl flex flex-col items-center justify-center p-2 border transition-all ${
                piece.completed
                  ? 'bg-gradient-to-br from-indigo-500/30 to-purple-500/30 border-indigo-500/50 shadow-lg shadow-indigo-500/10'
                  : 'bg-white/5 border-white/10 opacity-40'
              }`}
            >
              <span className="text-2xl">{piece.emoji}</span>
              <span className="text-[10px] text-gray-300 mt-1 text-center leading-tight">{piece.label}</span>
            </motion.div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-4">
          <motion.div
            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-coral-500 rounded-full"
            animate={{ width: `${(completedCount / pieces.length) * 100}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>

        {/* Reveal reasons for completed pieces */}
        <AnimatePresence>
          {pieces.filter((p) => p.completed).length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-2"
            >
              {pieces
                .filter((p) => p.completed)
                .map((piece) => (
                  <motion.div
                    key={piece.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-start gap-2 p-2 rounded-lg bg-white/5"
                  >
                    <span className="text-sm">{piece.emoji}</span>
                    <p className="text-xs text-gray-400">{piece.reason}</p>
                  </motion.div>
                ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* All Complete Celebration */}
        {allComplete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-4 p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-center"
          >
            <span className="text-3xl">🏆</span>
            <p className="text-green-300 font-bold mt-2">Developer DNA Complete!</p>
            <p className="text-gray-400 text-sm mt-1">
              You&apos;ve experienced the full stack of what makes a senior engineer: from catching syntax bugs →
              understanding language internals → protecting business logic → following process → orchestrating AI. This
              is the order because each layer builds on the previous one.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page Component ──────────────────────────────────────────────────────
const WhatIDoPage = () => {
  const [puzzlePieces, setPuzzlePieces] = useState<PuzzlePiece[]>(PUZZLE_PIECES);

  const completePiece = useCallback((id: string) => {
    setPuzzlePieces((prev) => prev.map((p) => (p.id === id ? { ...p, completed: true } : p)));
  }, []);

  return (
    <div className="min-h-screen pb-20 pt-10">
      {/* Hero Section */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16 pt-20">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">My role</h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto">My Role</p>
      </motion.div>

      {/* Puzzle Tracker - Sticky */}
      <div className="max-w-6xl mx-auto mb-12">
        <PuzzleTracker pieces={puzzlePieces} />
      </div>

      {/* Interactive Modules */}
      <div className="space-y-20 max-w-6xl mx-auto">
        {/* Module 1: Bug Hunt - Async & Syntax */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <CodeChallenge
            {...CHALLENGE_1}
            onComplete={() => completePiece('bugs')}
            completed={puzzlePieces[0].completed}
          />
        </motion.section>

        {/* Module 2: Scope & Hoisting */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <CodeChallenge
            {...CHALLENGE_2}
            onComplete={() => completePiece('scope')}
            completed={puzzlePieces[1].completed}
          />
        </motion.section>

        {/* Module 3: Idempotency & Transactions */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <CodeChallenge
            {...CHALLENGE_3}
            onComplete={() => completePiece('transactions')}
            completed={puzzlePieces[2].completed}
          />
        </motion.section>

        {/* Module 4: Dev Journey */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <DevJourney onComplete={() => completePiece('journey')} completed={puzzlePieces[3].completed} />
        </motion.section>

        {/* Module 5: AI Co-op Game */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <AICoopGame onComplete={() => completePiece('ai')} completed={puzzlePieces[4].completed} />
        </motion.section>
      </div>
    </div>
  );
};

export default WhatIDoPage;
